// Backend convention for `days_of_week`: Monday=0 … Sunday=6.

export interface MuteRule {
  id: string;
  notification_type: 'DAILY_SYNC';
  days_of_week: number[] | null;
  from_time: string | null;
  to_time: string | null;
  all_day: boolean;
  skip_us_holidays: boolean;
  enabled: boolean;
}

const FULL_DAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const SHORT_DAY_NAMES = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const;

const HHMM_PATTERN = /^(\d{1,2}):(\d{2})$/;

function isValidHHMM(value: string | null | undefined): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  const match = HHMM_PATTERN.exec(value);
  if (!match) {
    return false;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return (
    Number.isFinite(hours) &&
    hours >= 0 &&
    hours <= 23 &&
    Number.isFinite(minutes) &&
    minutes >= 0 &&
    minutes <= 59
  );
}

/** Render an `HH:MM` string as 12-hour with AM/PM. */
function formatTime12Hour(hhmm: string): string {
  const match = HHMM_PATTERN.exec(hhmm);
  if (!match) {
    return hhmm;
  }
  const hours24 = Number(match[1]);
  const minutes = match[2];
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${minutes} ${period}`;
}

/** Parse a valid `HH:MM` (or `H:MM`) string into total minutes since midnight. */
function toMinutes(hhmm: string): number {
  const match = HHMM_PATTERN.exec(hhmm);
  if (!match) {
    return 0;
  }
  return Number(match[1]) * 60 + Number(match[2]);
}

/**
 * Normalize `days_of_week` to a sorted, deduplicated list of valid indices
 * (0–6). Collapses null/undefined input, an empty array, and all-seven-days
 * arrays to `null` ("every day"). Out-of-range values are filtered out so
 * bad data cannot render as `undefined` in the UI.
 */
function normalizeDays(days: number[] | null | undefined): number[] | null {
  if (days == null) {
    return null;
  }
  const unique = Array.from(new Set(days))
    .filter(d => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);
  if (unique.length === 0 || unique.length === 7) {
    return null;
  }
  return unique;
}

/**
 * Render the "which days" portion of a rule. `allDay` shifts the phrasing
 * slightly — a rule that fires all day on weekdays reads as
 * "Every weekday (Mon–Fri)", whereas a timed rule reads as "Weekdays".
 */
function formatDayLabel(
  days: number[] | null | undefined,
  allDay: boolean,
): string {
  const normalized = normalizeDays(days);
  if (normalized == null) {
    return 'Every day';
  }

  const key = normalized.join(',');
  if (key === '5,6') {
    return allDay ? 'Every weekend (Sat & Sun)' : 'Weekends';
  }
  if (key === '0,1,2,3,4') {
    return allDay ? 'Every weekday (Mon–Fri)' : 'Weekdays';
  }
  if (normalized.length === 1) {
    return `Every ${FULL_DAY_NAMES[normalized[0]]}`;
  }
  return normalized.map(d => SHORT_DAY_NAMES[d]).join(', ');
}

/**
 * Render the time portion of a rule, or `null` if the rule has no valid
 * time information and isn't marked `all_day` (a malformed shape).
 */
function formatTimeWindow(rule: MuteRule): string | null {
  if (rule.all_day) {
    return 'all day';
  }

  const { from_time, to_time } = rule;

  if (isValidHHMM(from_time) && isValidHHMM(to_time)) {
    const from = formatTime12Hour(from_time);
    const to = formatTime12Hour(to_time);
    const isOvernight = toMinutes(from_time) > toMinutes(to_time);
    return isOvernight ? `${from} – ${to} (overnight)` : `${from} – ${to}`;
  }
  if (isValidHHMM(from_time)) {
    return `from ${formatTime12Hour(from_time)}`;
  }
  if (isValidHHMM(to_time)) {
    return `until ${formatTime12Hour(to_time)}`;
  }
  return null;
}

/**
 * Defensive last-resort renderer for a rule shape we don't recognize
 * (e.g. a malformed row that has neither a time window nor `all_day`).
 * The formatter must never throw, so we fall back to a short JSON summary.
 */
function safeFallback(rule: MuteRule): string {
  const summary = JSON.stringify({
    days_of_week: rule.days_of_week,
    from_time: rule.from_time,
    to_time: rule.to_time,
    all_day: rule.all_day,
    skip_us_holidays: rule.skip_us_holidays,
  });
  return `Mute rule (${summary})`;
}

/**
 * Convert a mute rule to a plain-English description for display.
 *
 * Examples:
 *   - `Every weekend (Sat & Sun), all day`
 *   - `Weekdays, 6:00 PM – 8:00 AM (overnight)`
 *   - `Every day, from 3:00 PM`
 *   - `Every US federal holiday, all day` (when skip_us_holidays=true and
 *     no days/times are set)
 */
export function formatMuteRule(rule: MuteRule): string {
  const hasFrom = isValidHHMM(rule.from_time);
  const hasTo = isValidHHMM(rule.to_time);
  // Use the normalized form so inputs that semantically mean "every day"
  // (null, empty, all-7-days, or all-invalid) are treated as "no days set"
  // for the US-federal-holidays shortcut below.
  const hasDays = normalizeDays(rule.days_of_week) != null;

  // Pure US federal holidays rule — no days, no times, not all-day.
  if (
    rule.skip_us_holidays &&
    !hasDays &&
    !hasFrom &&
    !hasTo &&
    !rule.all_day
  ) {
    return 'Every US federal holiday, all day';
  }

  const timeLabel = formatTimeWindow(rule);
  if (timeLabel == null) {
    return safeFallback(rule);
  }

  const dayLabel = formatDayLabel(rule.days_of_week, rule.all_day);
  const base = `${dayLabel}, ${timeLabel}`;
  // Combined case (day/time rule that also mutes on US holidays) is unused in
  // production today but the formatter should still render it truthfully.
  return rule.skip_us_holidays
    ? `${base} (includes US federal holidays)`
    : base;
}
